import $ from 'jquery';
import _ from 'lodash';
import axios from 'axios';
import audioHelper from './audio-helper';
import postEffects from './postEffects';
import { AudioSystem } from './audio-system';

export default {
	data() {
		let lastTime = 0;
		Object.defineProperty(this, 'lastTime', {
			get: () => lastTime,
			set: (v) => {
				lastTime = v;
				if (this.lastTimeChanged)
					this.lastTimeChanged(v);
			}
		});

		let currentTime = 0;
		Object.defineProperty(this, 'currentTime', {
			get: () => currentTime,
			set: (v) => {
				currentTime = v;
				if (this.canPlay({ allowModal: true }) || this.toolsTrack || !this.tracks?.length)
					this.lastTime = v;
				if (!this.isPlaying) {
					this.$forceUpdate();
				}
			}
		});

		return {
			trackSourcesData: {},
			trackSources: {},
			trackSourcesState: {},
			trackEffectsPromises: {},
			isPlaying: false,
			isPlayingInitiating: false,
			contextPlayingStartTime: 0,
			updateCurrentTimeTimer: null,
			playSessionId: null,
			masterTrackEffects: null,
			masterTrackSource: null,
			watermarkAudioBufferPromise: null,
			watermarkPlayingSource: null,
			pressedKeys: [],
			audioSystem: null
		};
	},
	watch: {
		isPlaying(v) {
			if (!v) {
				if (this.masterTrackEffects.onended)
					this.masterTrackEffects.onended();
				this.masterTrackEffects?.listeners?.map(f => {
					if (f?.name === 'ended') {
						f.callback();
					}
				});
			}
		},
		pressedKeys() {
			this.$emit('pressedKeys', this.pressedKeys);
		},
		soloSubTrackIds(v) {
			if (this.audioSystem) {
				this.audioSystem.soloSubTrackIds = v;
			}
		},
		soloTrackId(v) {
			if (this.audioSystem) {
				this.audioSystem.soloTrackId = v;
			}
		}
	},
	mounted() {
		if (!this.preventKeyHandles)
			this.initKeyHandles();
		this.$play = _.debounce(this.play, 100, {
			'leading': false,
			'trailing': true
		});
	},
	destroyed() {
		delete this.$play;
		delete this.$lastPlayInitPosition;
	},
	methods: {
		async initAudioContext() {
			if (__DEBUG__)
				console.debug('Init audio context');

			this.audioContext = new AudioContext();
			this.tracksEffects = {};

			return this.audioContext;
		},
		async initTrackSource(track, position, opts) {
			if (!this.audioSystem) {
				this.initAudioSystem();
			}

			return await this.audioSystem.initTrackSource(track, position, opts);
		},
		async initMasterTrack(context) {
			if (!this.audioSystem) {
				this.initAudioSystem();
			}

			this.masterTrackSource = await this.audioSystem.initMasterTrack(context);
			this.masterTrackEffects = this.masterTrackSource.effects;
			return this.masterTrackSource;
		},
		getTrackCanPlayed(trackId) {
			if (!this.audioSystem) {
				this.initAudioSystem();
			}

			return this.audioSystem.getTrackCanPlayed(trackId);
		},
		initAudioSystem(opts) {
			if (!this.audioSystem) {
				this.audioSystem = new AudioSystem();
			}

			this.audioSystem.clearListeners();

			this.audioSystem.init({
				audioContext: opts?.audioContext || this.audioContext,
				isTrial: this.$auth.isTrial(),
				trackSources: this.trackSources,
				trackSourcesData: this.trackSourcesData,
				tracksBuffers: this.tracksBuffers,
				trackEffectsPromises: this.trackEffectsPromises,
				isPlaying: this.isPlaying,
				tracks: this.tracks,
				masterTrackConfig: this.masterTrackConfig,
				aggregatedTracks: this.aggregatedTracks,
				soloSubTrackIds: this.soloSubTrackIds,
				soloTrackId: this.soloTrackId,
				onPlay: () => {
					this.isPlaying = true;
				},
				onStop: () => {
					this.isPlaying = false;
				},
				onMasterTrackInit: (source) => {
					this.masterTrackSource = source;
					this.masterTrackEffects = this.masterTrackSource.effects;
				}
			});
		},
		async play(position, opts) {
			opts = opts || {};

			if (this.audioSystem) {
				await this.audioSystem.processed();
			}

			await this.initAudioContext();

			let hasRange = this.rangeSelection.visible && this.rangeSelection.end !== this.rangeSelection.start && this.rangeSelection.end !== null;
			if (hasRange) {
				position = this.rangeSelectionArea[0] || 0;
				opts = {
					...(opts || {}),
					endTime: this.rangeSelectionArea[1]
				}

				if (__DEBUG__)
					console.debug('Range selection play applied', { opts, position });
			}

			opts.repeat = this.playbackFunctions?.repeat;
			
			this.initAudioSystem(opts);

			this.$lastPlayInitPosition = position;

			this.updateCurrentTimeTimer = setInterval(() => {
				if (this.isPlaying)
					this.currentTime = this.audioContext.currentTime - this.contextPlayingStartTime + 10/1000;
			}, 10);

			this.isPlayingInitiating = true;

			await this.audioSystem.play(position, opts).catch(ex => {
				console.error(ex);
				this.$notifyError(ex);
				this.audioSystem = null;
			}).finally(() => this.isPlayingInitiating = false);

			if (opts?.endTime) {
				this.audioSystem.addEventListener('stop', () => {
					this.currentTime = opts?.endTime;
				});
			}

			this.audioSystem.addEventListener('ended', async () => {
				if (__DEBUG__)
					console.debug('Test ended 1');
				if (this.playbackFunctions.repeat && this.audioSystem.isPlaying) {
					if (!hasRange) {
						await this.stop();
						this.play(0);
					} else {
						this.contextPlayingStartTime = this.audioSystem.audioContext.currentTime - position;
					}
				}
			});

			this.contextPlayingStartTime = this.audioSystem.audioContext.currentTime - position;

			this.$eventsBus.$emit('audio-tracker-play');
		},
		async stop(preventGoToStart) {
			if (this.audioSystem) {
				if (this.audioSystem) {
					await this.audioSystem.processed();
				}
				await this.audioSystem.stop();
				this.isPlaying = false;

				if (!preventGoToStart)
					this.currentTime = this.$lastPlayInitPosition || 0;

				this.$eventsBus.$emit('audio-tracker-stop');
				return;
			}
		},
		async togglePlay(forcedValue, preventGoToStart) {
			if (__DEBUG__)
				console.debug('Toggle play', { total: this.totalDuration, cur: this.currentTime, isPlaying: this.isPlaying });

			if (this.totalDuration > 0 && parseInt(this.totalDuration * 10) <= parseInt(this.currentTime * 10))
				await this.play(0);
			else
				if ((forcedValue === void 0 || forcedValue === true) && !this.isPlaying) {
					let tool = this.refsTools()?.selection;
					if (this.refsTools() && this.refsTools().selectionVisible && tool.duration) {
						await this.play(tool.start, { duration: tool.duration, tracks: [this.toolsTrack], solo: true });
					} else {
						await this.play(this.currentTime);
					}
				} else {
					this.stop(preventGoToStart === void 0 || preventGoToStart);
				}
		},
		async refreshPlay() {
			if (this.isPlaying)
				await this.play(this.currentTime);
		},
		async backward() {
			if (this.isPlaying) {
				await this.play(0);
			} else {
				this.currentTime = 0;
				this.calculateNavigatorPositionByCurrentTime(true, true);
			}
		},
		initKeyHandles() {
			const isInput = () => {
				var activeElement = document.activeElement;
				var inputs = ['input', 'select', 'button', 'textarea'];
				var classes = activeElement ? [...activeElement.classList] : [];

				return activeElement && (inputs.indexOf(activeElement.tagName.toLowerCase()) > -1 || classes.includes('jodit-wysiwyg'));
			}

			$('body').keydown(e => {
				if (this._isDestroyed || isInput()) return;
				if (this.canPlay({ allowModal: true })) {
					if (__DEBUG__)
						console.debug('KeyDown', { e });
					if (e.keyCode === 32) {
						this.togglePlay(void 0, false);
					}
					if (e.keyCode === 8) {
						this.backward();
					}
					if (e.keyCode === 9) {
						this.goNextBeat();
						e.stopPropagation();
						e.preventDefault();
					}
					if (['+', '='].includes(e.key)) {
						this.zoom(2);
					}
					if (e.key === '-') {
						this.zoom(0.5);
					}
					if (e.code === 'KeyA') {
						this.navigateThroughTracks(-1, 'horizontal');
					}
					if (e.code === 'KeyD') {
						this.navigateThroughTracks(1, 'horizontal');
					}
					if (e.code === 'KeyW') {
						this.navigateThroughTracks(-1, 'vertical');
					}
					if (e.code === 'KeyS') {
						this.navigateThroughTracks(1, 'vertical');
					}
				}
				if (!this.pressedKeys.includes(e.keyCode)) {
					this.pressedKeys.push(e.keyCode);
				}
			});

			$('body').keyup(e => {
				if (this._isDestroyed || isInput()) return;
				var activeElement = document.activeElement;
				var inputs = ['input', 'select', 'button', 'textarea'];

				if (activeElement && inputs.indexOf(activeElement.tagName.toLowerCase()) > -1) {
					return;
				}
				const idx = this.pressedKeys.findIndex(f => f === e.keyCode);
				if (idx > -1) {
					this.pressedKeys.splice(idx, 1);
				}
			})
		},
		navigateThroughTracks(direction, orientation) {
			if (__DEBUG__)
				console.debug('Navigate through tracks', { direction, orientation });
			let aggTrack = this.focusedAggregatedTrack || this.aggregatedTracks[0];
			let trackIdx = this.aggregatedTracks.findIndex(f => f === aggTrack) || 0;
			if (orientation === 'vertical') {
				this.focusedAggregatedTrack = this.aggregatedTracks[Math.max(0, Math.min(this.aggregatedTracks.length - 1, trackIdx + direction))];
			}
			if (orientation === 'horizontal') {
				let activeTrack = _.orderBy(aggTrack?.__tracks.filter(f => direction > 0 ? (f.end > this.currentTime) : (f.start < this.currentTime)) || [], f => f.start, [direction > 0 ? 'asc' : 'desc'])[0];

				if (!activeTrack) {
					this.currentTime = direction > 0 ? this.totalDuration : 0;
				} else {
					if (direction > 0) {
						if (activeTrack.start > this.currentTime) {
							this.currentTime = activeTrack.start;
						} else {
							this.currentTime = activeTrack.start + activeTrack.trimmedDuration;
						}
					} else {
						if (activeTrack.start + activeTrack.trimmedDuration < this.currentTime) {
							this.currentTime = activeTrack.start + activeTrack.trimmedDuration;
						} else {
							this.currentTime = activeTrack.start;
						}
					}
				}
				this.calculateNavigatorPositionByCurrentTime(true, true);
			}
		},
		async goNextBeat() {
			let aggTrack = this.focusedAggregatedTrack || this.aggregatedTracks[0];
			let activeTrack = _.orderBy(aggTrack?.__tracks.filter(f => f.end > this.currentTime) || [], f => f.start)[0];
			if (!activeTrack)
				activeTrack = aggTrack.__tracks[aggTrack.__tracks.length - 1];
			if (__DEBUG__)
				console.debug('Go next beat', { activeTrack, ct: this.currentTime, at: activeTrack?.start, bpm: activeTrack?.__bpm });
			if (activeTrack) {
				let delta = 0;
				let nextPoint = await audioHelper.getNextBeatPoint(activeTrack.__buffer, this.audioContext, Math.max(this.currentTime - activeTrack.start, 0), 60 / (activeTrack.__bpm || 128) * 4);
				if (this.currentTime < activeTrack.start) {
					delta = activeTrack.start - this.currentTime/* + (nextPoint || activeTrack.__bpmInfo?.beatStart || 0)*/;
				} else {
					if (nextPoint) {
						delta = nextPoint;
					} else if (activeTrack.__bpm) {
						delta = 60 / activeTrack.__bpm;
					}
				}
				if (delta) {
					if (this.isPlaying) {
						this.play(this.currentTime + delta);
					} else {
						this.currentTime += delta;
						this.calculateNavigatorPositionByCurrentTime(true, true);
					}
				}
			}
		},
		bufferToWave(abuffer, len) {
			return audioHelper.bufferToWave(abuffer, len);
		},
		downloadFinalAudio(opts) {
			return this.generateFinalAudio(opts).then(r => {
				return this.downloadBlob(r.wav, `${this.finalAudioName}.wav`);
			})
		},
		downloadBlob(blob, name) {
			let new_file = URL.createObjectURL(blob);

			let link = document.createElement('a');
			link.href = new_file;
			link.download = name;
			link.click();

			return new_file;
		},
		async generateFinalAudio(opts) {
			this.initAudioSystem();

			if (this.audioSystem) return await this.audioSystem.generateFinalAudio(opts);
		},
		async previewPlugins(opts) {
			if (!this.audioSystem) {
				this.initAudioSystem();
			}

			return await this.audioSystem.previewPlugins(opts);
		},
		stopPreviewPlugins() {
			if (!this.audioSystem) {
				this.initAudioSystem();
			}

			return this.audioSystem.stopPreviewPlugins();
		}
	}
};
